import { Component, inject, ElementRef, ViewChild, AfterViewChecked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { CopilotService } from '../../services/copilot.service';
import { ProvidAudioRecorderService } from '../../services/provid-audio-recorder.service';
import { CardCasoTatico, CopilotAction, ModoOperacaoIa } from '../../models/copilot.model';

@Component({
  selector: 'app-copilot-drawer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
    <!-- BACKDROP COM BLUR -->
    @if (copilot.isDrawerOpen()) {
      <div 
        class="fixed inset-0 bg-pmdf-bg-page/70 backdrop-blur-sm z-50 transition-opacity duration-300 animate-fade-in"
        (click)="copilot.closeDrawer()">
      </div>
    }

    <!-- DRAWER LATERAL (CYBER-MILITAR PMDF TÁTICO) -->
    <aside 
      class="copilot-drawer-shell fixed top-0 right-0 h-full w-full sm:w-[480px] lg:w-[530px] z-50 flex flex-col transform transition-transform duration-300 ease-in-out text-pmdf-base-primary"
      [class.translate-x-0]="copilot.isDrawerOpen()"
      [class.translate-x-full]="!copilot.isDrawerOpen()">

      <!-- HEADER CYBER-MILITAR PMDF COM SELETOR DE MODO -->
      <header class="copilot-header p-4 flex flex-col gap-3 shadow-sm">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="copilot-icon-box w-10 h-10 rounded-pmdf-card p-0.5 shadow-pmdf-card flex items-center justify-center">
              <div class="w-full h-full bg-pmdf-primary-strong rounded-pmdf-sm flex items-center justify-center icon-cyan">
                <svg class="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div>
              <h3 class="font-bold text-sm text-pmdf-base-primary tracking-wide uppercase">Copiloto PROVID</h3>
              <p class="text-xs text-pmdf-base-secondary/80 mt-0.5">Inteligência Tática & Decisão Operacional</p>
            </div>
          </div>

          <div class="flex items-center gap-1">
            <!-- BOTÃO LIMPAR HISTÓRICO -->
            <button 
              type="button" 
              (click)="copilot.limparHistorico()" 
              class="header-action-btn p-2 rounded-pmdf-card transition-colors"
              title="Limpar Conversa">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>

            <!-- BOTÃO FECHAR -->
            <button 
              type="button" 
              (click)="copilot.closeDrawer()" 
              class="header-action-btn p-2 rounded-pmdf-card transition-colors"
              title="Fechar Copiloto">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- SELETOR DE MODO IA / INTERCALAÇÃO (SEGMENTED SWITCH) -->
        <div class="ia-mode-selector flex items-center p-1 rounded-full text-xs font-bold shadow-inner">
          <button 
            type="button"
            (click)="copilot.setModoIa('ONLINE_LLM')"
            [class.ia-mode-active-llm]="copilot.isOnlineLlm()"
            class="ia-mode-btn flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-full transition-all text-[11px]"
            title="Usar Modelo Generativo GPT via Serviço Centralizado PMDF (planus-ia-backend)">
            <span class="w-2 h-2 rounded-full dot-emerald" [class.animate-ping]="copilot.isOnlineLlm()"></span>
            <span>IA Generativa GPT</span>
          </button>
          <button 
            type="button"
            (click)="copilot.setModoIa('NATIVO_DETERMINISTICO')"
            [class.ia-mode-active-analytic]="!copilot.isOnlineLlm()"
            class="ia-mode-btn flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-full transition-all text-[11px]"
            title="Usar Motor Analítico Nativo PROVID (Regras e Queries no MongoDB PMDF)">
            <span class="w-2 h-2 rounded-full dot-cyan" [class.animate-pulse]="!copilot.isOnlineLlm()"></span>
            <span>IA Analítica PROVID</span>
          </button>
        </div>
      </header>

      <!-- ATALHOS RÁPIDOS / PROMPT PILLS -->
      <div class="copilot-pills-bar px-3.5 py-2.5 flex items-center gap-2 overflow-x-auto no-scrollbar">
        <button 
          (click)="enviarPronta('Quais são os casos com arma de fogo na circunscrição?')" 
          class="copilot-pill shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-all shadow-sm flex items-center gap-1.5">
          <span>🚨</span>
          <span>Casos com Arma</span>
        </button>
        <button 
          (click)="enviarPronta('Mostrar visitas em atraso na UPM')" 
          class="copilot-pill shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-all shadow-sm flex items-center gap-1.5">
          <span>⏱️</span>
          <span>Visitas em Atraso</span>
        </button>
        <button 
          (click)="enviarPronta('Quais as estatísticas gerais do mês?')" 
          class="copilot-pill shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-all shadow-sm flex items-center gap-1.5">
          <span>📊</span>
          <span>Estatísticas</span>
        </button>
        <button 
          (click)="enviarPronta('Gerar minuta de relatório situacional para o Comando')" 
          class="copilot-pill shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-all shadow-sm flex items-center gap-1.5">
          <span>📄</span>
          <span>Relatório</span>
        </button>
      </div>

      <!-- FEED DE MENSAGENS -->
      <div #messageFeed class="flex-1 p-4 overflow-y-auto space-y-4 font-sans text-sm">
        @for (msg of copilot.historico(); track msg.id) {
          @if (msg.role === 'user') {
            <!-- MENSAGEM DO USUÁRIO -->
            <div class="flex justify-end">
              <div class="msg-user-bubble max-w-[85%] rounded-2xl rounded-tr-xs text-pmdf-base-primary px-4 py-3 shadow-pmdf-card">
                <p class="whitespace-pre-wrap leading-relaxed">{{ msg.content }}</p>
                <span class="text-[10px] time-subtle block text-right mt-1.5 font-mono">{{ msg.timestamp | date:'HH:mm' }}</span>
              </div>
            </div>
          } @else {
            <!-- RESPOSTA DO ASSISTENTE COPILOT -->
            <div class="flex gap-2.5 items-start animate-fade-in">
              <div class="copilot-avatar-icon w-8 h-8 rounded-pmdf-card flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <div class="flex-1 max-w-[92%] space-y-2.5">
                <!-- BALÃO PRINCIPAL DA RESPOSTA -->
                <div class="msg-assistant-bubble rounded-2xl rounded-tl-xs p-4 shadow-pmdf-card backdrop-blur-sm">
                  
                  <!-- FERRAMENTAS EXECUTADAS (TOOL CALLING CHIPS) -->
                  @if (msg.toolsExecutadas && msg.toolsExecutadas.length > 0) {
                    <div class="tool-list-container flex flex-wrap items-center gap-1.5 mb-3 pb-2.5">
                      <span class="tool-list-label text-[10px] uppercase font-bold flex items-center gap-1">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Ferramentas:
                      </span>
                      @for (tool of msg.toolsExecutadas; track tool) {
                        <span class="tool-chip inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono">
                          {{ tool }}
                        </span>
                      }
                    </div>
                  }

                  <!-- CONTEÚDO FORMATADO COM MARKDOWN NATIVO -->
                  <div class="markdown-content" [innerHTML]="renderMarkdown(msg.content)"></div>

                  <!-- RODAPÉ DA MENSAGEM & RLHF FEEDBACK -->
                  <div class="msg-footer flex items-center justify-between mt-3.5 pt-2.5 text-[10px] text-pmdf-base-secondary/80">
                    <div class="flex items-center gap-2">
                      <span class="brand-subtle font-semibold">PMDF PROVID</span>
                      <span>&bull;</span>
                      <span class="font-mono">{{ msg.timestamp | date:'HH:mm' }}</span>
                      @if (msg.modoOperacao === 'ONLINE_LLM') {
                        <span class="copilot-badge-llm px-1.5 py-0.2 rounded-full text-[9px]">GPT</span>
                      } @else if (msg.modoOperacao === 'NATIVO_DETERMINISTICO') {
                        <span class="copilot-badge-analytic px-1.5 py-0.2 rounded-full text-[9px]">Analítica</span>
                      }
                    </div>

                    <!-- FEEDBACK INTERATIVO -->
                    <div class="flex items-center gap-1.5">
                      <span class="text-[10px] text-pmdf-base-secondary/70">Esta resposta foi útil?</span>
                      <button 
                        type="button"
                        (click)="copilot.enviarFeedback(msg.id, true)"
                        [class.feedback-active-yes]="msg.feedbackUtil === true"
                        class="feedback-btn p-1 rounded transition-all"
                        title="Resposta útil">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                        </svg>
                      </button>
                      <button 
                        type="button"
                        (click)="copilot.enviarFeedback(msg.id, false)"
                        [class.feedback-active-no]="msg.feedbackUtil === false"
                        class="feedback-btn p-1 rounded transition-all"
                        title="Resposta não útil">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76 1.04m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-9h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- CARDS TÁTICOS (CASOS CRÍTICOS) -->
                @if (msg.cards && msg.cards.length > 0) {
                  <div class="space-y-2 mt-2">
                    <h5 class="tactical-heading text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Casos Mapeados para Ação Imediata
                    </h5>
                    @for (card of msg.cards; track card.processoId || card.processoNumero) {
                      <div class="tactical-card p-3.5 rounded-pmdf-card text-xs space-y-2 shadow-pmdf-card">
                        <div class="flex items-start justify-between gap-2">
                          <div>
                            <span class="font-mono font-bold text-pmdf-base-primary text-sm tracking-wide">{{ card.processoNumero }}</span>
                            <p class="text-pmdf-base-secondary text-[11px] mt-0.5">
                              <span class="text-pmdf-muted-30">Vítima:</span> <b class="text-pmdf-base-primary">{{ card.vitimaNome }}</b> 
                              <span class="mx-1.5">&bull;</span>
                              <span class="text-pmdf-muted-30">Autor:</span> <b class="text-pmdf-base-primary">{{ card.autorNome }}</b>
                            </p>
                          </div>
                          @if (card.grauRisco === 'EXTREMO') {
                            <span class="risk-badge-extremo px-2.5 py-0.5 rounded text-[10px] font-black tracking-wider">EXTREMO</span>
                          } @else if (card.grauRisco === 'ALTO') {
                            <span class="risk-badge-alto px-2.5 py-0.5 rounded text-[10px] font-black tracking-wider">ALTO</span>
                          } @else {
                            <span class="risk-badge-medio px-2.5 py-0.5 rounded text-[10px] font-bold">{{ card.grauRisco }}</span>
                          }
                        </div>

                        <div class="flex flex-wrap items-center gap-2 text-[11px]">
                          @if (card.possuiArma) {
                            <span class="tag-weapon px-2 py-0.5 rounded font-semibold flex items-center gap-1">
                              ⚠️ Arma de Fogo Sinalizada
                            </span>
                          }
                          @if (card.diasSemVisita) {
                            <span class="tag-delay px-2 py-0.5 rounded font-semibold">
                              ⏱️ {{ card.diasSemVisita }} dias sem visita
                            </span>
                          }
                        </div>

                        @if (card.recomendacaoTatica) {
                          <div class="tactical-callout p-2.5 rounded-pmdf-card text-xs leading-relaxed flex items-start gap-1.5">
                            <span class="callout-icon font-bold shrink-0 mt-0.5">🎯</span>
                            <span>{{ card.recomendacaoTatica }}</span>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }

                <!-- BOTÕES DE AÇÃO NA INTERFACE -->
                @if (msg.acoes && msg.acoes.length > 0) {
                  <div class="flex flex-wrap gap-2 mt-2">
                    @for (acao of msg.acoes; track acao.tipo) {
                      <button 
                        (click)="copilot.executarAcao(acao)"
                        class="action-btn-cta px-3.5 py-1.5 rounded-pmdf-card text-xs font-bold text-pmdf-base-primary shadow-sm transition-all flex items-center gap-1.5">
                        <span>⚡</span>
                        <span>{{ obterTextoAcao(acao) }}</span>
                      </button>
                    }
                  </div>
                }
              </div>
            </div>
          }
        }

        <!-- INDICADOR DE DIGITAÇÃO / PROCESSAMENTO -->
        @if (copilot.isLoading()) {
          <div class="flex gap-2.5 items-start animate-fade-in">
            <div class="copilot-avatar-icon w-8 h-8 rounded-pmdf-card flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
            </div>
            <div class="typing-bubble rounded-2xl rounded-tl-xs p-3.5 text-pmdf-base-secondary text-xs flex items-center gap-2.5 shadow-pmdf-card">
              <span class="w-2 h-2 rounded-full dot-cyan animate-bounce"></span>
              <span class="w-2 h-2 rounded-full dot-cyan animate-bounce [animation-delay:0.2s]"></span>
              <span class="w-2 h-2 rounded-full dot-cyan animate-bounce [animation-delay:0.4s]"></span>
              <span class="typing-text">
                @if (copilot.isOnlineLlm()) {
                  Processando via IA Generativa GPT (planus-ia-backend)...
                } @else {
                  Consultando base analítica PROVID via MongoDB...
                }
              </span>
            </div>
          </div>
        }
      </div>

      <!-- INPUT BAR COM MICROFONE / GRAVAÇÃO DE VOZ -->
      <footer class="copilot-footer p-3.5 flex flex-col gap-2">
        <!-- BARRA DE STATUS DE GRAVAÇÃO / TRANSCRIÇÃO ATIVA -->
        @if (audioRecorder.isRecording()) {
          <div class="audio-recording-bar flex flex-col gap-2 px-3.5 py-2.5 rounded-pmdf-card animate-pulse border border-pmdf-danger/50 bg-pmdf-danger-soft text-xs">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="w-3 h-3 rounded-full bg-pmdf-danger animate-ping"></span>
                <span class="font-bold text-pmdf-danger font-mono">GRAVANDO ÁUDIO: {{ audioRecorder.formatDuration(audioRecorder.durationSeconds()) }}</span>
              </div>
              <div class="flex items-center gap-2">
                <button 
                  type="button" 
                  (click)="cancelarGravacao()" 
                  class="px-2 py-1 rounded bg-pmdf-bg-muted text-pmdf-muted-70 hover:bg-pmdf-border text-[11px] font-semibold transition-colors"
                  title="Cancelar gravação">
                  Cancelar
                </button>
                <button 
                  type="button" 
                  (click)="concluirGravacaoETranscrever()" 
                  class="px-2.5 py-1 rounded bg-pmdf-success hover:bg-pmdf-success/90 text-pmdf-base-primary text-[11px] font-bold flex items-center gap-1 shadow-xs transition-colors"
                  title="Finalizar e Transcrever com Whisper PMDF">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Transcrever
                </button>
              </div>
            </div>
            @if (audioRecorder.transcricaoAoVivo()) {
              <div class="p-2 rounded bg-pmdf-bg-page border border-pmdf-border-subtle text-xs text-pmdf-base-primary italic font-sans leading-relaxed">
                "{{ audioRecorder.transcricaoAoVivo() }}"
              </div>
            }
          </div>
        } @else if (audioRecorder.isTranscribing()) {
          <div class="audio-transcribing-bar flex items-center gap-2 px-3.5 py-2 rounded-pmdf-card border border-pmdf-primary/40 bg-pmdf-info-soft text-xs text-pmdf-primary animate-pulse">
            <svg class="w-4 h-4 animate-spin text-pmdf-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
            </svg>
            <span class="font-semibold">Transcrevendo áudio via Whisper Corporativo PMDF (planus-ia-backend)...</span>
          </div>
        }

        @if (audioRecorder.errorMessage()) {
          <div class="text-[11px] text-pmdf-danger px-2 py-1 bg-pmdf-danger-soft rounded border border-pmdf-danger/40 flex items-center justify-between">
            <span>{{ audioRecorder.errorMessage() }}</span>
            <button type="button" (click)="audioRecorder.errorMessage.set(null)" class="text-xs text-pmdf-muted-55 hover:text-pmdf-text-primary">&times;</button>
          </div>
        }

        <form (ngSubmit)="enviar()" class="relative flex items-center gap-1.5">
          <div class="relative flex-1">
            <input 
              type="text" 
              [(ngModel)]="textoInput" 
              name="chatInput" 
              placeholder="Pergunte sobre processos, armas, visitas, ou use o microfone..."
              [disabled]="copilot.isLoading() || audioRecorder.isRecording() || audioRecorder.isTranscribing()"
              class="copilot-input w-full rounded-pmdf-card pl-4 pr-12 py-3 text-xs sm:text-sm text-pmdf-base-primary placeholder-pmdf-muted-55 focus:outline-none transition-all disabled:opacity-50" />
            
            <button 
              type="submit" 
              [disabled]="!textoInput.trim() || copilot.isLoading() || audioRecorder.isRecording()"
              class="send-btn absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-pmdf-card text-pmdf-base-primary disabled:opacity-30 transition-all shadow-xs"
              title="Enviar mensagem">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>

          <!-- BOTÃO DE MICROFONE INTERATIVO -->
          <button 
            type="button" 
            (click)="toggleGravacao()" 
            [disabled]="copilot.isLoading() || audioRecorder.isTranscribing()"
            [class.mic-active]="audioRecorder.isRecording()"
            class="mic-record-btn p-3 rounded-pmdf-card transition-all flex items-center justify-center shrink-0 shadow-xs"
            [title]="audioRecorder.isRecording() ? 'Parar e transcrever gravação' : 'Gravar áudio e transcrever por voz (Whisper IA)'">
            @if (audioRecorder.isRecording()) {
              <svg class="w-5 h-5 text-pmdf-danger animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            } @else {
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            }
          </button>
        </form>

        <div class="flex items-center justify-between text-[10px] text-pmdf-base-secondary/60 px-1 font-mono">
          <span>Pressione <b>Enter</b> para enviar &bull; 🎙️ <b>Microfone</b> para ditar</span>
          <span>🛡️ Sigilo PMDF &bull; LGPD Ativa</span>
        </div>
      </footer>
    </aside>
  `,
  styles: [`
    .copilot-drawer-shell {
      background-color: var(--pmdf-bg-page, #0A0F1D);
      border-left: 1px solid rgba(14, 165, 233, 0.3);
      box-shadow: -10px 0 35px rgba(0, 0, 0, 0.6);
    }

    .copilot-header {
      background-color: rgba(13, 23, 42, 0.95);
      border-bottom: 1px solid rgba(14, 165, 233, 0.2);
    }

    .copilot-icon-box {
      background: linear-gradient(135deg, #0284c7 0%, #1d4ed8 100%);
    }

    .icon-cyan {
      color: #38bdf8;
    }

    .ia-mode-selector {
      background-color: rgba(10, 15, 29, 0.9);
      border: 1px solid rgba(14, 165, 233, 0.25);
    }

    .ia-mode-btn {
      color: var(--pmdf-base-secondary, #94a3b8);
      &:hover:not(.ia-mode-active-llm):not(.ia-mode-active-analytic) {
        color: #f1f5f9;
        background-color: rgba(14, 165, 233, 0.12);
      }
    }

    .ia-mode-active-llm {
      background-color: rgba(6, 78, 59, 0.95);
      color: #6ee7b7 !important;
      border: 1px solid rgba(16, 185, 129, 0.6);
      box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
    }

    .ia-mode-active-analytic {
      background-color: rgba(8, 47, 73, 0.95);
      color: #7dd3fc !important;
      border: 1px solid rgba(14, 165, 233, 0.6);
      box-shadow: 0 0 10px rgba(14, 165, 233, 0.3);
    }

    .copilot-badge-llm {
      background-color: rgba(6, 78, 59, 0.85);
      color: #6ee7b7;
      border: 1px solid rgba(16, 185, 129, 0.4);
    }

    .copilot-badge-analytic {
      background-color: rgba(8, 47, 73, 0.85);
      color: #7dd3fc;
      border: 1px solid rgba(14, 165, 233, 0.4);
    }

    .dot-emerald {
      background-color: #34d399;
    }

    .dot-cyan {
      background-color: #38bdf8;
    }

    .header-action-btn {
      color: var(--pmdf-base-secondary, #94a3b8);
      &:hover {
        color: #38bdf8;
        background-color: rgba(14, 165, 233, 0.15);
      }
    }

    .copilot-pills-bar {
      background-color: rgba(13, 23, 42, 0.7);
      border-bottom: 1px solid rgba(14, 165, 233, 0.15);
    }

    .copilot-pill {
      background-color: rgba(15, 23, 42, 0.8);
      border: 1px solid rgba(14, 165, 233, 0.25);
      color: var(--pmdf-base-primary, #f1f5f9);
      &:hover {
        background-color: rgba(8, 47, 73, 0.9);
        color: #38bdf8;
        border-color: rgba(56, 189, 248, 0.6);
        transform: translateY(-1px);
      }
    }

    .msg-user-bubble {
      background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
      color: #ffffff;
      border: 1px solid rgba(56, 189, 248, 0.4);
    }

    .msg-assistant-bubble {
      background-color: rgba(15, 23, 42, 0.9);
      border: 1px solid rgba(14, 165, 233, 0.25);
      color: var(--pmdf-base-primary, #f1f5f9);
    }

    .copilot-avatar-icon {
      background-color: rgba(14, 165, 233, 0.15);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #38bdf8;
    }

    .time-subtle {
      color: rgba(255, 255, 255, 0.7);
    }

    .brand-subtle {
      color: #38bdf8;
    }

    .feedback-btn {
      color: var(--pmdf-base-secondary, #94a3b8);
      &:hover {
        color: #f1f5f9;
        background-color: rgba(14, 165, 233, 0.15);
      }
    }

    .feedback-active-yes {
      color: #34d399 !important;
      background-color: rgba(16, 185, 129, 0.2) !important;
    }

    .feedback-active-no {
      color: #f87171 !important;
      background-color: rgba(239, 68, 68, 0.2) !important;
    }

    .tool-list-container {
      border-bottom: 1px solid rgba(14, 165, 233, 0.2);
    }

    .tool-list-label {
      color: #38bdf8;
    }

    .tool-chip {
      background-color: rgba(14, 165, 233, 0.15);
      border: 1px solid rgba(14, 165, 233, 0.35);
      color: #7dd3fc;
    }

    .tactical-heading {
      color: #f59e0b;
    }

    .tactical-card {
      background-color: rgba(10, 15, 29, 0.85);
      border: 1px solid rgba(14, 165, 233, 0.3);
    }

    .tactical-callout {
      background-color: rgba(8, 47, 73, 0.6);
      border: 1px solid rgba(14, 165, 233, 0.25);
      color: #bae6fd;
    }

    .risk-badge-extremo {
      background-color: rgba(220, 38, 38, 0.25);
      color: #fca5a5;
      border: 1px solid rgba(239, 68, 68, 0.5);
    }

    .risk-badge-alto {
      background-color: rgba(217, 119, 6, 0.25);
      color: #fcd34d;
      border: 1px solid rgba(245, 158, 11, 0.5);
    }

    .risk-badge-medio {
      background-color: rgba(3, 105, 161, 0.25);
      color: #7dd3fc;
      border: 1px solid rgba(14, 165, 233, 0.5);
    }

    .tag-weapon {
      background-color: rgba(220, 38, 38, 0.2);
      color: #fca5a5;
      border: 1px solid rgba(239, 68, 68, 0.4);
    }

    .tag-delay {
      background-color: rgba(217, 119, 6, 0.2);
      color: #fcd34d;
      border: 1px solid rgba(245, 158, 11, 0.4);
    }

    .tactical-action-btn {
      background: linear-gradient(135deg, rgba(2, 132, 199, 0.25) 0%, rgba(29, 78, 216, 0.25) 100%);
      border: 1px solid rgba(14, 165, 233, 0.4);
      color: #38bdf8;
      &:hover {
        background: linear-gradient(135deg, rgba(2, 132, 199, 0.45) 0%, rgba(29, 78, 216, 0.45) 100%);
        border-color: rgba(56, 189, 248, 0.7);
        color: #ffffff;
      }
    }

    .typing-bubble {
      background-color: rgba(15, 23, 42, 0.7);
      border: 1px solid rgba(14, 165, 233, 0.2);
    }

    .typing-text {
      color: var(--pmdf-base-secondary, #94a3b8);
    }

    .copilot-footer {
      background-color: rgba(13, 23, 42, 0.95);
      border-top: 1px solid rgba(14, 165, 233, 0.2);
    }

    .copilot-input {
      background-color: rgba(10, 15, 29, 0.95);
      border: 1px solid rgba(14, 165, 233, 0.35);
      &:focus {
        border-color: #38bdf8;
        box-shadow: 0 0 12px rgba(56, 189, 248, 0.25);
      }
    }

    .send-btn {
      background: linear-gradient(135deg, #0284c7 0%, #1d4ed8 100%);
      &:hover:not(:disabled) {
        transform: scale(1.05);
        box-shadow: 0 0 10px rgba(14, 165, 233, 0.4);
      }
    }

    .mic-record-btn {
      background-color: rgba(10, 15, 29, 0.95);
      border: 1px solid rgba(14, 165, 233, 0.35);
      color: #38bdf8;
      &:hover:not(:disabled) {
        background-color: rgba(14, 165, 233, 0.15);
        border-color: #38bdf8;
        transform: scale(1.05);
      }
      &.mic-active {
        background-color: rgba(220, 38, 38, 0.2);
        border-color: #ef4444;
        box-shadow: 0 0 12px rgba(239, 68, 68, 0.4);
      }
    }

    :host ::ng-deep .markdown-content {
      color: #EFF6FF;
      line-height: 1.6;
      font-size: 0.875rem;

      p {
        margin-bottom: 0.6rem;
        &:last-child {
          margin-bottom: 0;
        }
      }

      strong, b {
        color: #38bdf8;
        font-weight: 700;
      }

      ul, ol {
        margin: 0.4rem 0 0.6rem 1.2rem;
        padding: 0;
      }

      ul {
        list-style-type: disc;
      }

      ol {
        list-style-type: decimal;
      }

      li {
        margin-bottom: 0.35rem;
      }

      code {
        background-color: rgba(14, 165, 233, 0.2);
        color: #38bdf8;
        padding: 0.15rem 0.35rem;
        border-radius: 4px;
        font-family: monospace;
        font-size: 0.8rem;
      }
    }
  `]
})
export class CopilotDrawerComponent implements AfterViewChecked {
  readonly copilot = inject(CopilotService);
  readonly audioRecorder = inject(ProvidAudioRecorderService);
  private readonly sanitizer = inject(DomSanitizer);

  @ViewChild('messageFeed') private messageFeedContainer?: ElementRef;

  textoInput: string = '';

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  renderMarkdown(content: string): SafeHtml {
    if (!content) return '';
    try {
      const parsed = marked.parse(content, { async: false }) as string;
      return this.sanitizer.bypassSecurityTrustHtml(parsed);
    } catch {
      return content;
    }
  }

  enviar(): void {
    if (!this.textoInput.trim()) return;
    const msg = this.textoInput;
    this.textoInput = '';
    this.copilot.enviarMensagem(msg);
  }

  enviarPronta(texto: string): void {
    this.copilot.enviarMensagem(texto);
  }

  async toggleGravacao(): Promise<void> {
    if (this.audioRecorder.isRecording()) {
      await this.concluirGravacaoETranscrever();
    } else {
      try {
        await this.audioRecorder.startRecording();
      } catch (err) {
        console.error('[CopilotDrawer] Erro ao iniciar gravação:', err);
      }
    }
  }

  cancelarGravacao(): void {
    this.audioRecorder.cancelRecording();
  }

  async concluirGravacaoETranscrever(): Promise<void> {
    try {
      const resp = await this.audioRecorder.stopAndTranscribe();
      const transcricao = resp.transcricaoLiteral || resp.relatoFormatadoMarkdown || '';
      if (transcricao.trim()) {
        this.textoInput = (this.textoInput ? this.textoInput + ' ' : '') + transcricao.trim();
      }
    } catch (err) {
      console.error('[CopilotDrawer] Erro na transcrição de áudio:', err);
    }
  }

  obterTextoAcao(acao: CopilotAction): string {
    if (acao.tipo === 'SET_FILTER') return 'Aplicar Filtro na Grade';
    if (acao.tipo === 'NAVIGATE') return 'Acessar Painel';
    if (acao.tipo === 'EXPORT_REPORT') return 'Emitir Relatório Oficial';
    return 'Executar Ação';
  }

  private scrollToBottom(): void {
    if (this.messageFeedContainer) {
      try {
        this.messageFeedContainer.nativeElement.scrollTop = this.messageFeedContainer.nativeElement.scrollHeight;
      } catch (err) {
        // Ignora erro suave de scroll
      }
    }
  }
}
